/**
 * HeroShellLoading.tsx - Suspense Fallback for Hero Section
 *
 * Renders a loading skeleton that matches the HeroShell layout to prevent
 * layout shift (CLS) during lazy component loading. This component is used
 * as the Suspense fallback when transitioning from shell to full platform.
 *
 * Structure matches HeroShell.tsx:
 * - .hero-immersive wrapper (100svh min-height)
 * - Background layers (.hero-bg-layer.hero-bg-base, .hero-bg-accent)
 * - .omnibar-container with centered content
 * - Loading indicator inside input area
 *
 * @see .planning/research/ARCHITECTURE.md - Shell Loading Fallback
 * @see .planning/research/PITFALLS.md - Section #7: Shell Pattern Layout Shift
 */

import React from "react";

// Import the same CSS as HeroShell to ensure consistent styling
import "@/shared/components/full-site/styles/hero-shared.css";
import "@/marketing/components/full-site/styles/hero-immersive.desktop.css";

// ============================================================================
// LOADING SPINNER STYLES (inline for reliability)
// ============================================================================

const spinnerContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  padding: "16px 24px",
  width: "100%",
  boxSizing: "border-box",
};

const spinnerStyle: React.CSSProperties = {
  width: "20px",
  height: "20px",
  border: "2px solid rgba(63, 157, 161, 0.2)",
  borderTopColor: "#3f9da1", // --color-accent
  borderRadius: "50%",
  animation: "hero-loading-spin 0.8s linear infinite",
};

const loadingTextStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--color-text-dim, #64748b)",
  fontFamily: "var(--font-sans)",
};

// ============================================================================
// COMPONENT
// ============================================================================

export function HeroShellLoading(): JSX.Element {
  return (
    <div className="hero-immersive">
      {/* Inline keyframes for spinner animation */}
      <style>
        {`
          @keyframes hero-loading-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Background layers - same as HeroShell */}
      <div className="hero-bg-layer hero-bg-base" />
      <div className="hero-bg-layer hero-bg-accent" />
      <div className="hero-bg-layer hero-vignette" />

      {/* Omnibar Container - matches shell positioning */}
      <div className="omnibar-container">
        {/* Headline placeholder - reserves space */}
        <div className="hero-headline-wrapper">
          <div
            style={{
              height: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </div>

        {/* Tagline placeholder */}
        <div
          className="hero-tagline"
          style={{
            visibility: "hidden",
            height: "24px",
          }}
        />

        {/* Input wrapper with loading state */}
        <div className="omnibar-input-wrapper">
          <div className="omnibar-glow" />
          <div className="omnibar-input-container">
            <div style={spinnerContainerStyle}>
              <div style={spinnerStyle} aria-hidden="true" />
              <span style={loadingTextStyle}>Loading platform...</span>
            </div>
          </div>
        </div>

        {/* Suggestion chips placeholder */}
        <div
          className="suggestion-chips"
          style={{
            visibility: "hidden",
            height: "32px",
          }}
        />
      </div>

      {/* Partners section placeholder - reserves bottom space */}
      <div
        className="partners-brokers-section"
        style={{
          visibility: "hidden",
        }}
      />
    </div>
  );
}

export default HeroShellLoading;
